import { inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import * as React from 'react';

import { AnalyticsStore, ExperimentStore, RouteStore } from 'bernie-core';
import { Context } from 'bernie-context';
import { ViewLarge, ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { MultiItemStore } from 'stores/multi-item-store';
import { SearchResultsStore } from 'stores/search-results-store';
import { ShortlistStore } from 'stores/shortlist-store';
import { UIPageStore } from 'stores/ui-page-store';

import { HotelViewModel, PropertyListingBadgeModel } from 'models';

import { UitkFigureAspectRatioType } from 'uitk-react-images';

import { alternateDatesInfo } from 'utils/listing-utils';
import { onClickScrollToTarget } from 'utils/smooth-scroll';

import { DefaultListing } from './internals/default-listing';
import { HorizontalListing } from './internals/horizontal-listing';
import { PropertyFields } from 'src/common/__generated__/api';
import { Title, TitleProps } from './internals/title';

export interface ListingComponentProps {
  'data-stid'?: string;
  analytics?: AnalyticsStore;
  breakfastIncluded?: string;
  className?: string;
  context?: Context;
  currentHotel?: CurrentHotelStore;
  dataAttributes?: any;
  defaultListingOnly?: boolean;
  displayViewed?: boolean;
  endDate?: string;
  excludePriceBreakDownLink?: boolean;
  experiment?: ExperimentStore;
  history?: { push: (submitUrl: string) => void };
  index?: number;
  isDatelessPdp?: boolean;
  lazyLoadImg?: boolean;
  loading?: boolean;
  mapListingView?: boolean;
  model: HotelViewModel;
  multiItem?: MultiItemStore;
  onFavoriteClick?: () => void;
  searchResults?: SearchResultsStore;
  source?: string;
  stars?: string;
  startDate?: string;
  referralObject?: {
    navigation?: {
      linkName?: string;
      rfrr?: string;
    };
    favorite?: {
      linkName?: string;
      rfrr?: string;
    };
    unfavorite?: {
      linkName?: string;
      rfrr?: string;
    };
    close?: {
      linkName?: string;
      rfrr?: string;
    };
  };
  routeStore?: RouteStore;
  setResultRef?: (ref: HTMLElement, index: number) => void;
  shortlist?: ShortlistStore;
  uiPage?: UIPageStore;
}

interface ListingComponentState {
  alternateDatesExpanded: boolean;
}

@withRouter
@inject('analytics', 'context', 'currentHotel', 'multiItem', 'routeStore', 'uiPage')
class ListingComponent extends React.Component<ListingComponentProps, ListingComponentState> {
  constructor(props) {
    super(props);

    this.navigateOnClient = this.navigateOnClient.bind(this);
  }
  private handleAdditionalInfoClick = () => {
    onClickScrollToTarget({ scrollTo: 0 });
  };

  private setItemRef = ref => {
    const { index, setResultRef } = this.props;
    if (setResultRef && typeof setResultRef === 'function') {
      setResultRef(ref, index);
    }
  };

  private listingClickHandler = (property: PropertyFields.Fragment) => {
    const { context, currentHotel } = this.props;

    if (!property) {
      return;
    }

    const isMobile = context.deviceInformation?.mobile && !context.deviceInformation?.tablet;

    if (property.sponsoredListing) {
      const sponsoredListing = property.sponsoredListing;

      if (sponsoredListing.clickRedirectLinkoffTrackingUrl) {
        window.open(sponsoredListing.clickRedirectLinkoffTrackingUrl);
        currentHotel.setPropertyListingAsCurrentHotel(property);

        return;
      } else if (isMobile) {
        currentHotel.setPropertyListingAsCurrentHotel(property);

        return;
      }
    }

    if (currentHotel?.setPropertyListingAsCurrentHotel) {
      currentHotel.setPropertyListingAsCurrentHotel(property);

      return;
    }
  };

  private getTitleComponent(imageRatio = UitkFigureAspectRatioType.R21_9, extraProps = {}) {
    const { context, displayViewed, lazyLoadImg, mapListingView, model, multiItem, onFavoriteClick, referralObject } = this.props;

    const { name, pinnedDetails, propertyListingBadgeModel, shortlistModel, thumbnail, travelAd } = model;

    const titleProps: TitleProps = {
      deviceInformation: context.deviceInformation,
      displayViewed,
      fallbackImageUrl: (thumbnail && thumbnail.fallBackImage) || '',
      imageUrl: (thumbnail && thumbnail.src) || '',
      lazyLoadImg,
      listingClickHandler: this.listingClickHandler,
      mapListingView,
      name,
      navigateOnClient: this.navigateOnClient,
      onFavoriteClick,
      packageType: multiItem.getPackageType(),
      pinned: pinnedDetails,
      propertyListingBadgeModel,
      ratio: imageRatio,
      referralObject,
      shortlistModel,
      travelAd,
    };

    return <Title {...titleProps} {...extraProps} />;
  }

  private navigateOnClient(relativePath: string) {
    this.props.history.push(relativePath);
  }

  private routeStoreGoBack = () => {
    this.props.routeStore.goBack();
  };

  private getDefaultListingTopMarginClass(propertyListingBadgeModel: PropertyListingBadgeModel): string {
    const secondaryBadgeIconExists =
      propertyListingBadgeModel &&
      propertyListingBadgeModel.secondary &&
      (propertyListingBadgeModel.secondary.icon || propertyListingBadgeModel.secondary.text);

    return secondaryBadgeIconExists ? 'all-t-margin-half' : '';
  }

  public render() {
    const {
      className,
      defaultListingOnly,
      index,
      loading,
      model,
      routeStore,
      isDatelessPdp,
      mapListingView,
      onFavoriteClick,
      excludePriceBreakDownLink,
      multiItem,
    } = this.props;
    const { alternateDates, propertyListingBadgeModel } = model;
    const defaultListingTopMarginClass = this.getDefaultListingTopMarginClass(propertyListingBadgeModel);
    const stars = isDatelessPdp ? model.comparisonModel && model.comparisonModel.starRating : model.stars;

    const alternateDatesProps = {
      handleClick: this.handleAdditionalInfoClick,
      model: alternateDates,
    };

    const cardLinkProps = { ...this.props };

    const commonListingProps = {
      alternateDatesInfo: alternateDatesInfo(),
      alternateDatesProps,
      index,
      loading,
      model,
      routeStore,
      isDatelessPdp,
      className,
      stars,
      getTitleComponent: this.getTitleComponent.bind(this),
      dataStid: this.props['data-stid'],
      cardLinkProps,
      routeStoreGoBack: this.routeStoreGoBack.bind(this),
      listingClickHandler: this.listingClickHandler.bind(this),
      setItemRef: this.setItemRef.bind(this),
      mapListingView,
      onFavoriteClick,
      excludePriceBreakDownLink,
      displaySecondaryBadge: !isDatelessPdp,
      multiItem,
    };

    const defaultListingProps = {
      ...commonListingProps,
      defaultListingTopMarginClass,
    };

    if (defaultListingOnly) {
      return <DefaultListing {...defaultListingProps} />;
    }

    return (
      <Viewport>
        <ViewSmall>
          <DefaultListing {...defaultListingProps} />
        </ViewSmall>
        <ViewMedium>
          <HorizontalListing {...commonListingProps} imageAspectRatio={`${UitkFigureAspectRatioType.R3_2}`} />
        </ViewMedium>
        <ViewLarge>
          <HorizontalListing {...commonListingProps} imageAspectRatio={`${UitkFigureAspectRatioType.R16_9}`} />
        </ViewLarge>
      </Viewport>
    );
  }
}

export const Listing = inject(
  'searchResults',
  'currentHotel',
  'context',
  'shortlist',
  'analytics',
  'experiment',
  'multiItem'
)(ListingComponent);
